import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Clock, ExternalLink, Search, ShoppingBag, Tag, XCircle } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import omit from 'lodash/omit';
import styled, { css } from 'styled-components';

import {
  GetOrderStatsQuery as GetOrderStats,
  Locale,
  OrderFilters as OrderFiltersType,
  ParticipantFieldScope,
  PurchasePaymentStatus,
} from '__generated__/graphql';

import { ParticipantFieldFilter } from '../Registrations/RegistrationFilters';
import { UrlState, parseHelpers } from '../../../common/useUrlState';
import { encodeUrl, isEmpty, toggleInList } from '../../../common/helpers';
import {
  syncParticipantFields, syncProductVariants, syncPromotions, syncTimeSlots,
} from '../../../common/filterHelpers';
import EventPicker, { Event as SelectedEvent } from '../../Events/EventPicker';
import GetOrderFiltersQuery from './GetOrderFiltersQuery';
import GetOrderStatsQuery from './GetOrderStatsQuery';
import ParticipantFieldFiltersForm, { ParticipantFieldFilters } from '../ParticipantFieldFiltersForm';
import SellableLabel from '../../../common/Common/SellableLabel';
import UI from '../../../common/UI';
import config from '../../config';
import useLocale from '../../../common/useLocale';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useTimeSlotFormatter from '../../../common/Tickets/useTimeSlotFormatter';

export type OrderStats = GetOrderStats['project']['order_stats'];

export interface OrderFilterParams {
  event?: string | null;
  search?: string | null;
  ticket_categories?: string[];
  tickets?: string[];
  time_slots?: string[];
  products?: string[];
  product_variants?: string[];
  promotions?: string[];
  invitations?: string[];
  discounts?: string[];
  coupons?: string[];
  lotteries?: string[];
  rejected_lotteries?: string[];
  created_after?: string | null;
  created_before?: string | null;
  payment_statuses?: PurchasePaymentStatus[];
  participant_fields?: ParticipantFieldFilter[];
  participant_field_choices?: string[];
  assigned?: boolean | null;
  mail_opt_ins?: string[];
  locales?: Locale[];
}

/**
 * Types of filters that can be applied to orders.
 * */
export type OrderFilterType = (
  'event' | 'search' | 'tickets' | 'time_slots' | 'products' | 'product_variants' | 'promotions' | 'payment_statuses'
  | 'participant_fields' | 'participant_field_choices' | 'invitations' | 'discounts' | 'coupons' | 'lotteries'
  | 'rejected_lotteries' | 'order_date' | 'assigned' | 'mail_opt_ins' | 'locales'
);

export interface OrderFiltersProps {
  types?: OrderFilterType[];
  filters: OrderFilterParams;
  onChange: (filters: OrderFilterParams) => unknown;
  onStatsChange?: (stats: OrderStats) => void;
  readOnly?: boolean;
  showTotals?: boolean;
  showViewOrdersButton?: boolean;
  showResetButton?: boolean;
}

const OrderFilters = ({
  types,
  filters,
  onChange,
  onStatsChange,
  readOnly = false,
  showTotals = true,
  showViewOrdersButton = false,
  showResetButton = true,
}: OrderFiltersProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();
  const formatTimeSlot = useTimeSlotFormatter();

  const enabled = (type: OrderFilterType) => !types || types.includes(type);

  const searchRef = useRef<HTMLInputElement>();

  // Fetches the filters (tickets etc.)
  const { data: filtersData } = useQuery(
    GetOrderFiltersQuery,
    {
      variables: {
        project_id: project.id,
        events: filters.event ? [filters.event] : [],
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const mailPermissions = filtersData?.project.mail_permissions.filter(
    (mailPermission) => !mailPermission.integration,
  ) || [];
  const events = filtersData?.project.events;
  const event = events?.data.filter(({ id }) => filters.event === id)[0] || null;

  const participantFields = useMemo(
    () => event?.participant_fields.filter(
      (field) => field.scope === ParticipantFieldScope.order,
    ) || [],
    [event],
  );

  const handleChange = useCallback((filters: OrderFilterParams) => {
    let newFilters = filters;
    newFilters = syncTimeSlots(newFilters, event);
    newFilters = syncProductVariants(newFilters, event);
    newFilters = syncPromotions(newFilters, event);
    newFilters = syncParticipantFields(newFilters, participantFields);
    onChange(newFilters);
  }, [event, participantFields, onChange]);

  const { mapOrderFiltersToServerInput } = useOrderFilterMapping();

  // Fetches the filters (tickets etc.)
  const { data: statsData, loading: loadingStats } = useQuery(
    GetOrderStatsQuery,
    {
      variables: {
        ...mapOrderFiltersToServerInput(filters),
        project_id: project.id,
      },
      fetchPolicy: 'cache-and-network',
      skip: !showTotals,
      onCompleted: (data) => {
        onStatsChange?.(data.project.order_stats);
      },
    },
  );

  const stats = statsData?.project.order_stats;

  const getDefaultShowingFilters = (filters: OrderFilterParams) => ({
    event: !!isEmpty(filters.event),
    search: !!filters.search,
    tickets: filters.ticket_categories?.length > 0 || filters.tickets?.length > 0 || filters.time_slots?.length
      || filters.promotions?.length > 0,
    time_slots: filters.time_slots?.length > 0,
    products: filters.products?.length > 0,
    product_variants: filters.product_variants?.length > 0,
    promotions: filters.promotions?.length > 0,
    invitations: filters.invitations?.length > 0,
    discounts: filters.discounts?.length > 0,
    coupons: filters.coupons?.length > 0,
    lotteries: filters.lotteries?.length > 0,
    rejected_lotteries: filters.rejected_lotteries?.length > 0,
    payment_statuses: filters.payment_statuses?.length > 0,
    participant_fields: filters.participant_fields?.length > 0,
    participant_field_choices: filters.participant_field_choices?.length > 0,
    order_date: !!filters.created_after || !!filters.created_before,
    assigned: typeof filters.assigned === 'boolean',
    mail_opt_ins: filters.mail_opt_ins?.length > 0,
    locales: filters.locales?.length > 0,
  });

  // Indicates whether a filter category is collapsed or not
  const [showingFilters, setShowingFilters] = useState(() => getDefaultShowingFilters(filters));

  /**
   * Shows/hides a filter category.
   */
  const toggleShowingFilters = (key: keyof typeof showingFilters) => {
    setShowingFilters((showing) => ({
      ...showing,
      [key]: !showing[key],
    }));
  };

  const selectEvent = (event?: SelectedEvent) => {
    // Reset all filters except search.
    handleChange({
      search: filters.search,
      event: event?.id,
    });
  };

  const handleCreatedAfterChange = (date: string | null) => {
    handleChange({
      ...filters,
      created_after: date,
    });
  };

  const handleCreatedBeforeChange = (date: string | null) => {
    handleChange({
      ...filters,
      created_before: date,
    });
  };

  /**
   * Handles changes to the search input by storing the value in the filters.
   */
  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    handleChange({
      ...filters,
      search: event.target.value || null,
    });
  };

  /**
   * Unsets the search term in the filters and empties the input.
   */
  const resetSearch = () => {
    handleChange({
      ...filters,
      search: null,
    });

    searchRef.current.value = '';
  };

  // Only the keys of OrderFilterParams that have an array as value
  type OrderFilterListType = {
    [K in keyof OrderFilterParams]-?: OrderFilterParams[K] extends Array<any> ? K : never
  }[keyof OrderFilterParams];

  /**
   * Toggles a filter in a list: if it's not in the list, it's added, otherwise it's removed.
   */
  const toggleListFilter = (name: OrderFilterListType, id: string) => {
    handleChange({
      ...filters,
      [name]: toggleInList([...filters[name] || []], id),
    });
  };

  /**
   * Toggles a ticket category filter.
   */
  const toggleTicketCategoryFilter = (ticketCategoryId: string) => {
    toggleListFilter('ticket_categories', ticketCategoryId);
  };

  /**
 * Toggles a ticket filter.
 */
  const toggleTicketFilter = (ticketId: string) => {
    if (event.ticket_categories.length === 1) {
      // If there is only one ticket category, the ticket category filter is not shown,
      // so it must be added/removed automatically.
      const newTicketIds = toggleInList([...filters.tickets || []], ticketId);

      handleChange({
        ...filters,
        ticket_categories: newTicketIds.length > 0 ? [event.ticket_categories[0].id] : [],
        tickets: newTicketIds,
      });
    } else {
      toggleListFilter('tickets', ticketId);
    }
  };

  /**
   * Toggles a locales filter.
   */
  const toggleLocaleFilter = (locales?: Locale) => {
    toggleListFilter('locales', locales);
  };

  /**
   * Toggles a time slot filter.
   */
  const toggleTimeSlotFilter = (timeSlotId: string) => {
    toggleListFilter('time_slots', timeSlotId);
  };

  /**
   * Toggles a product filter.
   */
  const toggleProductFilter = (productId: string) => {
    toggleListFilter('products', productId);
  };

  /**
   * Toggles a product variant filter.
   */
  const toggleProductVariantFilter = (productVariantId: string) => {
    toggleListFilter('product_variants', productVariantId);
  };

  /**
   * Toggles a promotion filter.
   */
  const togglePromotionFilter = (promotionId: string) => {
    toggleListFilter('promotions', promotionId);
  };

  /**
   * Toggles an invitation code filter.
   */
  const toggleInvitationFilter = (invitationId: string) => {
    toggleListFilter('invitations', invitationId);
  };

  /**
   * Toggles a discount filter.
   */
  const toggleDiscountFilter = (discountId: string) => {
    toggleListFilter('discounts', discountId);
  };

  /**
   * Toggles a coupon filter.
   */
  const toggleCouponFilter = (couponId: string) => {
    toggleListFilter('coupons', couponId);
  };

  /**
   * Toggles a lottery filter.
   */
  const toggleLotteryFilter = (lotteryId: string) => {
    toggleListFilter('lotteries', lotteryId);
  };

  /**
   * Toggles a rejected lottery filter.
   */
  const toggleRejectedLotteryFilter = (lotteryId: string) => {
    toggleListFilter('rejected_lotteries', lotteryId);
  };

  /**
   * Toggles a payment status filter.
   */
  const togglePaymentStatusFilter = (status: PurchasePaymentStatus) => {
    toggleListFilter('payment_statuses', status);
  };

  /**
     * Toggles a participant field filter.
     */
  const toggleParticipantFieldFilter = (participantFieldId: string) => {
    const index = (filters.participant_fields || []).findIndex(({ id }) => id === participantFieldId);

    const participantFields = index === -1
      ? [...filters.participant_fields || [], { id: participantFieldId }]
      : filters.participant_fields.filter(({ id }) => id !== participantFieldId);

    handleChange({
      ...filters,
      participant_fields: participantFields,
    });
  };

  /**
     * Updates the filter object for a participant field.
     */
  const setParticipantFieldFilters = (
    fieldId: string,
    update: (fieldFilters: ParticipantFieldFilters) => ParticipantFieldFilters,
  ) => {
    handleChange({
      ...filters,
      participant_fields: filters.participant_fields?.map((field) => (field.id === fieldId ? {
        ...field,
        filters: update(field.filters),
      } : field)),
    });
  };

  /**
    * Toggles the 'has unassigned tickets' filter
    */
  const toggleAssignedFilter = (value: boolean | null) => {
    handleChange({
      ...filters,
      assigned: value,
    });
  };

  /**
   * Toggles a participant field choice filter.
   */
  const toggleParticipantFieldChoiceFilter = (participantFieldChoiceId: string) => {
    toggleListFilter('participant_field_choices', participantFieldChoiceId);
  };

  /**
   * Indicates whether a user has activated any filters.
   */
  const hasFilters = Object.values(filters).some((param) => !isEmpty(param));

  /**
   * Resets all filters, UI state, and search input.
   */
  const resetFilters = (newFilters: OrderFilterParams = {}) => {
    handleChange(newFilters);
    setShowingFilters(getDefaultShowingFilters(newFilters));

    if (searchRef.current) {
      searchRef.current.value = newFilters?.search || '';
    }
  };

  return (
    <UI.FormGrid sc={{ gutter: 2 / 3 }}>
      <UI.Delimit delimiter={<UI.HR sc={{ dotted: true }} />}>
        {(enabled('search') || enabled('event')) && (
          <>
            {enabled('event') && (
              <UI.InputGroup>
                <EventPicker
                  eventId={filters.event}
                  onChange={selectEvent}
                  renderActive={(event) => (
                    <UI.Span sc={{ color: 'gray.800' }} style={{ fontWeight: '500' }}>
                      {event?.title || t('all_events')}
                    </UI.Span>
                  )}
                />
              </UI.InputGroup>
            )}

            {enabled('search') && (
              <UI.InputGroup>
                <UI.InputWrapper>
                  <UI.Span>
                    <UI.Icon>
                      <Search />
                    </UI.Icon>
                  </UI.Span>
                  <UI.DebouncedInput
                    type="text"
                    value={filters.search}
                    onChange={handleSearch}
                    placeholder={t('search_orders')}
                    ref={searchRef}
                    sc={{ px: 5 }}
                    disabled={readOnly}
                  />
                  {filters.search && (
                  <UI.Span>
                    <UI.A onClick={() => resetSearch()} sc={{ color: 'gray.400' }}>
                      <UI.Icon>
                        <XCircle />
                      </UI.Icon>
                    </UI.A>
                  </UI.Span>
                  )}
                </UI.InputWrapper>
              </UI.InputGroup>
            )}
          </>
        )}

        {filters.event && filters.event !== event?.id && (
          <UI.FlexContainer style={{ minHeight: 21 }} sc={{ alignItems: 'center' }}>
            <UI.Loader />
          </UI.FlexContainer>
        )}

        {event && event.ticket_categories.length > 0 && enabled('tickets') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('tickets')}
              role="button"
              sc={{ mb: showingFilters.tickets ? 2 : 0 }}
            >
              {t('tickets')}
              {' '}
              {!showingFilters.tickets && filters.tickets?.length > 0 && (
                <UI.Badge>{formatNumber(filters.tickets.length)}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.tickets && <ChevronUp />}
                {!showingFilters.tickets && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.tickets && event.ticket_categories.map((ticketCategory) => (
              <UI.Div key={ticketCategory.id}>
                {event.ticket_categories.length > 1 && (
                  <UI.Checkbox
                    checked={!!filters.ticket_categories?.includes(ticketCategory.id)}
                    onChange={() => toggleTicketCategoryFilter(ticketCategory.id)}
                    disabled={readOnly}
                  >
                    {ticketCategory.title}
                  </UI.Checkbox>
                )}
                {(event.ticket_categories.length === 1
                  || filters.ticket_categories?.includes(ticketCategory.id)) && (
                  <UI.Div sc={{ pl: event.ticket_categories.length > 1 ? 4 : 0 }}>
                    {ticketCategory.tickets.map((ticket) => (
                      <UI.Div key={ticket.id}>
                        <UI.Checkbox
                          checked={!!filters.tickets?.includes(ticket.id)}
                          onChange={() => toggleTicketFilter(ticket.id)}
                          disabled={readOnly}
                        >
                          {ticket.title}
                        </UI.Checkbox>
                        {filters.tickets?.includes(ticket.id)
                          && (ticket.promotions.length > 1 || ticket.time_slots.length > 0)
                          && (
                            <UI.Div sc={{ pl: 4 }}>
                              {ticket.promotions.length > 1 && ticket.promotions.map((promotion) => (
                                <UI.Checkbox
                                  checked={!!filters.promotions?.includes(promotion.id)}
                                  onChange={() => togglePromotionFilter(promotion.id)}
                                  disabled={readOnly}
                                  key={promotion.id}
                                >
                                  <UI.Icon sc={{ muted: true }}>
                                    <Tag />
                                  </UI.Icon>
                                  {' '}
                                  {promotion.title || t('standard')}
                                </UI.Checkbox>
                              ))}
                              {ticket.time_slots.length > 0 && ticket.time_slots.map((timeSlot) => (
                                <UI.Checkbox
                                  checked={!!filters.time_slots?.includes(timeSlot.id)}
                                  onChange={() => toggleTimeSlotFilter(timeSlot.id)}
                                  disabled={readOnly}
                                  key={timeSlot.id}
                                >
                                  <UI.Icon sc={{ muted: true }}>
                                    <Clock />
                                  </UI.Icon>
                                  {' '}
                                  <UI.Delimit>
                                    {formatTimeSlot(timeSlot)}
                                    {timeSlot.title}
                                  </UI.Delimit>
                                </UI.Checkbox>
                              ))}
                            </UI.Div>
                          )}
                      </UI.Div>
                    ))}
                  </UI.Div>
                )}
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {event && event.products.length > 0 && enabled('products') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('products')}
              role="button"
              sc={{ mb: showingFilters.products ? 2 : 0 }}
            >
              {t('products')}
              {' '}
              {!showingFilters.products && filters.products?.length > 0 && (
              <UI.Badge>{formatNumber(filters.products.length)}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.products && <ChevronUp />}
                {!showingFilters.products && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.products && event.products.map((product) => (
              <UI.Div key={product.id}>
                <UI.Checkbox
                  checked={!!filters.products?.includes(product.id)}
                  onChange={() => toggleProductFilter(product.id)}
                  disabled={readOnly}
                >
                  {product.title}
                </UI.Checkbox>
                {filters.products?.includes(product.id)
                    && (product.promotions.length > 1 || product.product_variants.length > 0)
                    && (
                      <UI.Div sc={{ pl: 4 }}>
                        {product.promotions.length > 1 && product.promotions.map((promotion) => (
                          <UI.Checkbox
                            checked={!!filters.promotions?.includes(promotion.id)}
                            onChange={() => togglePromotionFilter(promotion.id)}
                            disabled={readOnly}
                            key={promotion.id}
                          >
                            <UI.Icon sc={{ muted: true }}>
                              <Tag />
                            </UI.Icon>
                            {' '}
                            {promotion.title || t('standard')}
                          </UI.Checkbox>
                        ))}
                        {product.product_variants.length > 0
                          && product.product_variants.map((productVariant) => (
                            <UI.Checkbox
                              checked={!!filters.product_variants?.includes(productVariant.id)}
                              onChange={() => toggleProductVariantFilter(productVariant.id)}
                              disabled={readOnly}
                              key={productVariant.id}
                            >
                              <SellableLabel type="product">
                                {productVariant.title}
                              </SellableLabel>
                            </UI.Checkbox>
                          ))}
                      </UI.Div>
                    )}
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {participantFields.length > 0 && enabled('participant_fields') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('participant_fields')}
              role="button"
              sc={{ mb: showingFilters.participant_fields ? 2 : 0 }}
            >
              {t('custom_fields')}
              {' '}
              {!showingFilters.participant_fields
                && (filters.participant_field_choices?.length > 0
                || filters.participant_fields?.length > 0) && (
                <UI.Badge>
                  {(filters.participant_field_choices?.length || 0) + (filters.participant_fields?.length || 0)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.participant_fields && <ChevronUp />}
                {!showingFilters.participant_fields && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.participant_fields && (
              <UI.Div>
                {participantFields.map((field) => (
                  <UI.Div key={field.id}>
                    <UI.Checkbox
                      checked={!!filters.participant_fields?.map(({ id }) => id).includes(field.id)}
                      onChange={() => toggleParticipantFieldFilter(field.id)}
                      disabled={readOnly}
                    >
                      <UI.Span sc={{ muted: !field.enabled }}>
                        {field.title}
                      </UI.Span>
                    </UI.Checkbox>
                    {filters.participant_fields?.map(({ id }) => id).includes(field.id) && (
                      <UI.Div sc={{ pl: 4 }}>
                        <ParticipantFieldFiltersForm
                          field={field}
                          filters={
                            filters.participant_fields.filter(
                              ({ id }) => id === field.id,
                            )[0].filters
                          }
                          onChange={(update) => setParticipantFieldFilters(field.id, update)}
                        />
                        {field.choices.map((choice) => (
                          <UI.Checkbox
                            checked={!!filters.participant_field_choices?.includes(choice.id)}
                            onChange={() => toggleParticipantFieldChoiceFilter(choice.id)}
                            disabled={readOnly}
                            key={choice.id}
                          >
                            <UI.Span sc={{ muted: !choice.enabled }}>
                              {choice.title}
                            </UI.Span>
                          </UI.Checkbox>
                        ))}
                      </UI.Div>
                    )}
                  </UI.Div>
                ))}
              </UI.Div>
            )}
          </UI.InputGroup>
        )}

        {event && event.invitations.length > 0 && enabled('invitations') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('invitations')}
              role="button"
              sc={{ mb: showingFilters.invitations ? 2 : 0 }}
            >
              {t('invitation_codes')}
              {' '}
              {!showingFilters.invitations && filters.invitations?.length > 0 && (
              <UI.Badge>
                {formatNumber(filters.invitations.length)}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.invitations && <ChevronUp />}
                {!showingFilters.invitations && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.invitations && event.invitations.map((invitation) => (
              <UI.Div key={invitation.id}>
                <UI.Checkbox
                  checked={!!filters.invitations?.includes(invitation.id)}
                  onChange={() => toggleInvitationFilter(invitation.id)}
                  disabled={readOnly}
                >
                  <UI.Label>
                    <UI.Strong>{invitation.prefix}</UI.Strong>
                  </UI.Label>
                </UI.Checkbox>
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {event && event.discounts.length > 0 && enabled('discounts') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('discounts')}
              role="button"
              sc={{ mb: showingFilters.discounts ? 2 : 0 }}
            >
              {t('discounts')}
              {' '}
              {!showingFilters.discounts && filters.discounts?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.discounts.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.discounts && <ChevronUp />}
                {!showingFilters.discounts && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.discounts && event.discounts.map((discount) => (
              <UI.Div key={discount.id}>
                <UI.Checkbox
                  checked={!!filters.discounts?.includes(discount.id)}
                  onChange={() => toggleDiscountFilter(discount.id)}
                  disabled={readOnly}
                >
                  {discount.title}
                </UI.Checkbox>
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {event && event.coupons.length > 0 && enabled('coupons') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('coupons')}
              role="button"
              sc={{ mb: showingFilters.coupons ? 2 : 0 }}
            >
              {t('coupons')}
              {' '}
              {!showingFilters.coupons && filters.coupons?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.coupons.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.coupons && <ChevronUp />}
                {!showingFilters.coupons && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.coupons && event.coupons.map((coupon) => (
              <UI.Div key={coupon.id}>
                <UI.Checkbox
                  checked={!!filters.coupons?.includes(coupon.id)}
                  onChange={() => toggleCouponFilter(coupon.id)}
                  disabled={readOnly}
                >
                  <UI.Label>
                    <UI.Strong>{coupon.prefix}</UI.Strong>
                  </UI.Label>
                </UI.Checkbox>
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {event && event.lotteries.length > 0 && enabled('lotteries') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('lotteries')}
              role="button"
              sc={{ mb: showingFilters.lotteries ? 2 : 0 }}
            >
              {t('selected_by_lottery')}
              {' '}
              {!showingFilters.lotteries && filters.lotteries?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.lotteries.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.lotteries && <ChevronUp />}
                {!showingFilters.lotteries && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.lotteries && event.lotteries.map((lottery) => (
              <UI.Div key={lottery.id}>
                <UI.Checkbox
                  checked={!!filters.lotteries?.includes(lottery.id)}
                  onChange={() => toggleLotteryFilter(lottery.id)}
                  disabled={readOnly}
                >
                  {lottery.title}
                </UI.Checkbox>
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {event && event.lotteries.length > 0 && enabled('rejected_lotteries') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('rejected_lotteries')}
              role="button"
              sc={{ mb: showingFilters.rejected_lotteries ? 2 : 0 }}
            >
              {t('rejected_by_lottery')}
              {' '}
              {!showingFilters.rejected_lotteries && filters.rejected_lotteries?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.rejected_lotteries.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.rejected_lotteries && <ChevronUp />}
                {!showingFilters.rejected_lotteries && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.rejected_lotteries && event.lotteries.map((lottery) => (
              <UI.Div key={lottery.id}>
                <UI.Checkbox
                  checked={!!filters.rejected_lotteries?.includes(lottery.id)}
                  onChange={() => toggleRejectedLotteryFilter(lottery.id)}
                  disabled={readOnly}
                >
                  {lottery.title}
                </UI.Checkbox>
              </UI.Div>
            ))}
          </UI.InputGroup>
        )}

        {enabled('order_date') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('order_date')}
              role="button"
              sc={{ mb: showingFilters.order_date ? 2 : 0 }}
            >
              {t('order_date')}
              {' '}
              {!showingFilters.order_date && (filters.created_after || filters.created_before) && (
                <UI.Badge>
                  {formatNumber(
                    filters.created_after && filters.created_before ? 2 : (
                      filters.created_after || filters.created_before ? 1 : 0
                    ),
                  )}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.order_date && <ChevronUp />}
                {!showingFilters.order_date && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.order_date && (
              <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
                <UI.Div>
                  <UI.InputLabel htmlFor="CreatedAfter">
                    {t('date_after')}
                  </UI.InputLabel>
                  <UI.DatePicker
                    selected={filters.created_after}
                    maxDate={filters.created_before}
                    endDate={filters.created_before}
                    onChange={handleCreatedAfterChange}
                    disabled={readOnly}
                    id="CreatedAfter"
                  />
                </UI.Div>
                <UI.Div>
                  <UI.InputLabel htmlFor="CreatedBefore">
                    {t('date_before')}
                  </UI.InputLabel>
                  <UI.DatePicker
                    selected={filters.created_before}
                    minDate={filters.created_after}
                    startDate={filters.created_after}
                    onChange={handleCreatedBeforeChange}
                    disabled={readOnly}
                    id="CreatedBefore"
                  />
                </UI.Div>
              </UI.FormGrid>
            )}
          </UI.InputGroup>
        )}

        {enabled('assigned') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('assigned')}
              role="button"
              sc={{ mb: showingFilters.assigned ? 2 : 0 }}
            >
              {t('assignment_status')}
              {' '}
              {!showingFilters.assigned && typeof filters.assigned === 'boolean' && (
                <UI.Badge>
                  1
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.assigned && <ChevronUp />}
                {!showingFilters.assigned && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>

            {showingFilters.assigned && (
              <UI.Div>
                <UI.Radio
                  checked={filters.assigned === null}
                  onChange={() => toggleAssignedFilter(null)}
                  disabled={readOnly}
                >
                  {t('all')}
                </UI.Radio>
                <UI.Radio
                  checked={filters.assigned === false}
                  onChange={() => toggleAssignedFilter(false)}
                  disabled={readOnly}
                >
                  {t('has_unassigned_tickets')}
                </UI.Radio>
                <UI.Radio
                  checked={filters.assigned === true}
                  onChange={() => toggleAssignedFilter(true)}
                  disabled={readOnly}
                >
                  {t('has_assigned_tickets')}
                </UI.Radio>
              </UI.Div>
            )}
          </UI.InputGroup>
        )}

        {enabled('payment_statuses') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('payment_statuses')}
              role="button"
              sc={{ mb: showingFilters.payment_statuses ? 2 : 0 }}
            >
              {t('payment_status')}
              {' '}
              {!showingFilters.payment_statuses && filters.payment_statuses?.length > 0 && (
                <UI.Badge>
                  {formatNumber(filters.payment_statuses.length)}
                </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.payment_statuses && <ChevronUp />}
                {!showingFilters.payment_statuses && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.payment_statuses && (
              <UI.Div>
                <UI.Checkbox
                  checked={!!filters.payment_statuses?.includes(PurchasePaymentStatus.paid)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.paid)}
                  disabled={readOnly}
                >
                  {t('paid')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={!!filters.payment_statuses?.includes(PurchasePaymentStatus.open)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.open)}
                  disabled={readOnly}
                >
                  {t('open')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={!!filters.payment_statuses?.includes(PurchasePaymentStatus.refunded)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.refunded)}
                  disabled={readOnly}
                >
                  {t('refunded')}
                </UI.Checkbox>
                <UI.Checkbox
                  checked={!!filters.payment_statuses?.includes(PurchasePaymentStatus.has_balance)}
                  onChange={() => togglePaymentStatusFilter(PurchasePaymentStatus.has_balance)}
                  disabled={readOnly}
                >
                  {t('credit')}
                </UI.Checkbox>
              </UI.Div>
            )}
          </UI.InputGroup>
        )}

        {enabled('locales') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('locales')}
              role="button"
              sc={{ mb: showingFilters.locales ? 2 : 0 }}
            >
              {t('preferred_language')}
              {' '}
              {!showingFilters.locales && filters.locales?.length > 0 && (
              <UI.Badge>
                {formatNumber(filters.locales.length)}
              </UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.locales && <ChevronUp />}
                {!showingFilters.locales && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.locales && config.locales.map((locale) => (
              <UI.Checkbox
                key={`locales_${locale}`}
                checked={!!filters.locales?.includes(locale)}
                onChange={() => toggleLocaleFilter(locale)}
                disabled={readOnly}
              >
                {t(`common:locales.${locale}`)}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        )}

        {mailPermissions.length > 0 && enabled('mail_opt_ins') && (
          <UI.InputGroup>
            <CategoryHeading
              onClick={() => toggleShowingFilters('mail_opt_ins')}
              role="button"
              sc={{ mb: showingFilters.mail_opt_ins ? 2 : 0 }}
            >
              {t('mail_opt_ins')}
              {' '}
              {!showingFilters.mail_opt_ins && filters.mail_opt_ins?.length > 0 && (
                <UI.Badge>{formatNumber(filters.mail_opt_ins.length)}</UI.Badge>
              )}
              {' '}
              <UI.Icon sc={{ color: 'gray.500', ml: 1 }}>
                {showingFilters.mail_opt_ins && <ChevronUp />}
                {!showingFilters.mail_opt_ins && <ChevronDown />}
              </UI.Icon>
            </CategoryHeading>
            {showingFilters.mail_opt_ins && mailPermissions.map((mailPermission) => (
              <UI.Checkbox
                onChange={() => toggleListFilter('mail_opt_ins', mailPermission.id)}
                checked={!!filters.mail_opt_ins?.includes(mailPermission.id)}
                disabled={readOnly}
                key={mailPermission.id}
              >
                {mailPermission.title}
              </UI.Checkbox>
            ))}
          </UI.InputGroup>
        )}

        {(showTotals || showResetButton) && (
          <>
            {showTotals && (
              <UI.Card
                sc={{
                  spacing: 'xs',
                  outline: 'gray.200',
                  background: 'gray.50',
                  loading: loadingStats,
                  borderRadius: 'md',
                  small: true,
                }}
              >
                <UI.Delimit>
                  <UI.Span sc={{ color: 'gray.600', inline: true }} style={{ fontWeight: 500 }}>
                    <UI.Icon sc={{ mr: 1, mb: 0.25 }}>
                      {loadingStats ? <UI.Loader sc={{ size: 14 }} /> : <ShoppingBag />}
                    </UI.Icon>
                    <Trans
                      i18nKey="n_orders_in_selection"
                      values={{
                        count: stats?.orders || 0,
                        value: formatNumber(stats?.orders || 0),
                      }}
                    >
                      <UI.Strong />
                    </Trans>
                  </UI.Span>
                  {showViewOrdersButton && (
                    <UI.A href={`/b/${project.id}/participants/orders?${encodeUrl(filters)}`} target="_blank">
                      {t('view')}
                      {' '}
                      <UI.Icon sc={{ mb: 1 / 3 }}>
                        <ExternalLink />
                      </UI.Icon>
                    </UI.A>
                  )}
                </UI.Delimit>
              </UI.Card>
            )}

            {showResetButton && hasFilters && (
              <UI.Div>
                <UI.Button onClick={() => resetFilters()}>
                  {t('reset_filters')}
                </UI.Button>
              </UI.Div>
            )}
          </>
        )}
      </UI.Delimit>
    </UI.FormGrid>
  );
};

const CategoryHeading = styled(UI.A)`
  ${({ theme }) => css`
    color: ${theme.colors.gray[600]};
    font-weight: 500;
    display: block;
    line-height: 21px;
  `}
`;

export const useOrderFilterMapping = () => {
  const { getDateFilterMappers } = useLocale();
  const { dateAfterToServer, dateBeforeToServer, dateAfterToFilter, dateBeforeToFilter } = getDateFilterMappers();

  const mapOrderFiltersToServerInput = (filters: OrderFilterParams): OrderFiltersType => {
    const purchaseFilters = {
      ticket_categories: filters.ticket_categories ?? undefined,
      tickets: filters.tickets ?? undefined,
      time_slots: filters.time_slots ?? undefined,
      products: filters.products ?? undefined,
      product_variants: filters.product_variants ?? undefined,
      promotions: filters.promotions ?? undefined,
      invitations: filters.invitations ?? undefined,
      discounts: filters.discounts ?? undefined,
      coupons: filters.coupons ?? undefined,
      lotteries: filters.lotteries ?? undefined,
      rejected_lotteries: filters.rejected_lotteries ?? undefined,
      payment_statuses: filters.payment_statuses ?? undefined,
      assigned: filters.assigned ?? undefined,
    };

    const participantFilters = {
      locales: filters.locales ?? undefined,
      mail_opt_ins: filters.mail_opt_ins ?? undefined,
    };

    return {
      events: filters.event ? [filters.event] : undefined,
      search: filters.search ?? undefined,
      created_after: filters.created_after ? dateAfterToServer(filters.created_after) : undefined,
      created_before: filters.created_before ? dateBeforeToServer(filters.created_before) : undefined,
      participant_fields: filters.participant_fields ?? undefined,
      participant_field_choices: filters.participant_field_choices ?? undefined,

      // Subfilters
      purchase_filters: !isEmpty(purchaseFilters) ? purchaseFilters : undefined,
      participant_filters: !isEmpty(participantFilters) ? participantFilters : undefined,
    };
  };

  const mapServerOutputToOrderFilters = (filters: OrderFiltersType): OrderFilterParams => ({
    event: filters.events[0],
    search: filters.search ?? undefined,
    participant_fields: filters.participant_fields?.map((filter) => ({
      id: filter.id,
      filters: filter.filters ? omit(filter.filters, '__typename') : null,
    })) ?? undefined,
    participant_field_choices: filters.participant_field_choices ?? undefined,
    created_after: filters.created_after ? dateAfterToFilter(filters.created_after) : undefined,
    created_before: filters.created_before ? dateBeforeToFilter(filters.created_before) : undefined,

    // Purchase filters
    ticket_categories: filters.purchase_filters?.ticket_categories ?? undefined,
    tickets: filters.purchase_filters?.tickets ?? undefined,
    time_slots: filters.purchase_filters?.time_slots ?? undefined,
    products: filters.purchase_filters?.products ?? undefined,
    product_variants: filters.purchase_filters?.product_variants ?? undefined,
    promotions: filters.purchase_filters?.promotions ?? undefined,
    invitations: filters.purchase_filters?.invitations ?? undefined,
    discounts: filters.purchase_filters?.discounts ?? undefined,
    coupons: filters.purchase_filters?.coupons ?? undefined,
    lotteries: filters.purchase_filters?.lotteries ?? undefined,
    rejected_lotteries: filters.purchase_filters?.rejected_lotteries ?? undefined,
    payment_statuses: filters.purchase_filters?.payment_statuses ?? undefined,
    assigned: filters.purchase_filters?.assigned ?? undefined,

    // Participant filters
    locales: filters.participant_filters?.locales ?? undefined,
    mail_opt_ins: filters.participant_filters?.mail_opt_ins ?? undefined,
  });

  const parseOrderFiltersUrlState = (params: UrlState): OrderFilterParams => ({
    event: parseHelpers.string(params.event),
    search: parseHelpers.string(params.search),
    ticket_categories: parseHelpers.array(params.ticket_categories, parseHelpers.string, null),
    tickets: parseHelpers.array(params.tickets, parseHelpers.string, null),
    time_slots: parseHelpers.array(params.time_slots, parseHelpers.string, null),
    products: parseHelpers.array(params.products, parseHelpers.string, null),
    product_variants: parseHelpers.array(params.product_variants, parseHelpers.string, null),
    promotions: parseHelpers.array(params.promotions, parseHelpers.string, null),
    invitations: parseHelpers.array(params.invitations, parseHelpers.string, null),
    discounts: parseHelpers.array(params.discounts, parseHelpers.string, null),
    coupons: parseHelpers.array(params.coupons, parseHelpers.string, null),
    lotteries: parseHelpers.array(params.lotteries, parseHelpers.string, null),
    rejected_lotteries: parseHelpers.array(params.rejected_lotteries, parseHelpers.string, null),
    mail_opt_ins: parseHelpers.array(params.mail_opt_ins, parseHelpers.string, null),
    created_after: parseHelpers.date(params.created_after),
    created_before: parseHelpers.date(params.created_before),
    assigned: parseHelpers.boolean(params.assigned),
    locales: parseHelpers.array(params.locales, parseHelpers.string, null),
    payment_statuses: parseHelpers.array(params.payment_statuses, (value) => (
      parseHelpers.string(value) in PurchasePaymentStatus ? value : null),
    null,
    ),
    participant_fields: parseHelpers.array(
      params.participant_fields,
      (value) => (typeof value === 'object' ? value : null),
      null,
    ),
    participant_field_choices: parseHelpers.array(params.participant_field_choices, parseHelpers.string, null),
  });

  return {
    mapOrderFiltersToServerInput,
    mapServerOutputToOrderFilters,
    parseOrderFiltersUrlState,
  };
};

export default OrderFilters;
